import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  isDevMode,
  OnInit,
  runInInjectionContext,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, PRIMARY_OUTLET, Router, RouterOutlet } from '@angular/router';
import { TOKEN_BROADCAST_DI_TOKEN } from '@core/broadcast/broadcast';
import { BroadcastMessageType } from '@core/broadcast/broadcast.model';
import { Navbar } from '@core/components/navbar/navbar';
import { UserNotification } from '@core/dexie/dexie.model';
import { tryPersistWithoutPromtingUser } from '@core/dexie/persist-storage';
import { normalizeBackendTimestamp } from '@core/formatting';
import { LoggerService } from '@core/log/services/logger';
import { NetworkListener } from '@core/network/network-listener';
import { FirebasePushMgr } from '@core/notifications/services/firebase-push-mgr';
import { IndexedDbNotificationApi } from '@core/notifications/services/indexed-db-notification-api';
import { QueryOperatorDTOExtended } from '@core/operators/operator-query.model';
import { injectEnsureCachedLoggedOperator } from '@core/operators/state/operator.cache';
import { buildNavigationEndStream, ROUTE_LOADING_MIN_VISIBLE_MS } from '@core/router/route-loading-utils';
import { injectActivatedOutlets, injectCurrentUrl, injectRouteLoading } from '@core/router/route-signals';
import { keycloakAuthHandlerEffect } from '@core/sso/keycloak-auth-handler';
import { injectSessionKeepAlive } from '@core/sso/session-keep-alive';
import { injectSilentSsoRecovery } from '@core/sso/silent-sso-recovery';
import { injectVersionUpdateToast } from '@core/sw-updates/version-update-toast';
import { VersionUpdateToastContext } from '@core/sw-updates/version-update-toast.model';
import { injectInvalidateFailedQueries } from '@core/tanstack/failed-queries.cache';
import { injectApplyBroadcastInvalidations } from '@core/tanstack/tanstack-cache-utils';
import { toggleDevTools } from '@core/tanstack/tanstack.providers';
import { QuequeMgr } from '@core/utility/queque-mgr';
import { environment as Config } from '@env/environment';
import { TranslocoDirective } from '@jsverse/transloco';
import { AbsoluteLoader } from '@shared/components/loader/absolute-loader';
import { ProgressBar } from '@shared/components/progress-bar/progress-bar';
import { Button } from '@shared/directives/button/button';
import { Icon } from '@shared/directives/icons/iconify';
import { Splitter } from '@shared/components/splitter/splitter';
import { SplitterPanel } from '@shared/components/splitter/splitter-panel';
import { Nullable } from '@shared/models/types';
import { slideInTop } from '@shared/utility/animation-utils';
import { getFromStorage, removeFromStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import Keycloak from 'keycloak-js';
import { DeviceDetectorService } from 'ngx-device-detector';
import { isFullscreenAuthRoute } from '@core/router/fullscreen-auth-routes';
import { fromEvent, map, scan, switchMap } from 'rxjs';
import { AvailableOutlets } from './app.model';

type UserNotificationOmit = Omit<UserNotification, 'opeId'>;

@Component({
  selector: 'ta-root',
  template: `
    <ng-container *transloco="let translate">
      <div class="flex flex-col size-full">
        @if (this.showNavbar()) {
          <ta-navbar
            (toggleAiChat)="this.toggleAiChatHandler()"
            (toggleNotifications)="this.toggleNotificationsHandler()"
          />
        }
        @if (this.systemOffline()) {
          <div
            class="flex items-center justify-center gap-4 p-2 bg-tag-danger/20 text-tag-danger"
            [animate.enter]="this.slideInTopAnimation.enter"
            [animate.leave]="this.slideInTopAnimation.leave"
          >
            <span class="animate-pulse h-4 icon-fw" taIcon="wifiSlash"></span>
            <span class="animate-pulse">{{ translate('systemOffline') }}</span>
          </div>
        }
        <div class="flex-1 bg-tag-surface relative overflow-hidden">
          @if (this.isLoadingRoute()) {
            <ta-absolute-loader zIndex="5" />
          }
          <ta-splitter class="ta-splitter-home size-full relative isolate">
            <ta-splitter-panel [minSize]="this.minPanelSizes()[0]" [size]="this.panelSizes()[0]">
              <router-outlet />
            </ta-splitter-panel>
            <ta-splitter-panel
              [minSize]="this.minPanelSizes()[1]"
              [size]="this.panelSizes()[1]"
              [visible]="this.splitPanelDisplay() !== 'none'"
            >
              <router-outlet name="r-panel" />
            </ta-splitter-panel>
          </ta-splitter>
        </div>
        @if (this.embedded() === false) {
          <div class="flex justify-center w-full h-8 items-center text-sm text-tag-action-hover font-medium">
            <a class="hover:text-tag-action hover:underline" href="http://www.tagapplication.it" target="_blank">
              TAG Application powered by GRAF ENTERPRISE
            </a>
          </div>
        }
      </div>
    </ng-container>
    <router-outlet name="modal" />
    <router-outlet name="second-modal" />
    <ng-template #versionUpdateToast let-reload="reload" let-status="status">
      <div *transloco="let translate" class="flex flex-col p-4 gap-3 w-full">
        <div class="flex items-center gap-4">
          <span class="h-7 icon-fw" taIcon="circleInfo"></span>
          @switch (status) {
            @case ('downloading') {
              <span>{{ translate('versionInfo.downloading') }}</span>
            }
            @case ('ready') {
              <div class="flex flex-col gap-2 pr-4">
                <span class="font-semibold">{{ translate('versionInfo.newVersionAvailable') }}</span>
                <span>{{ translate('versionInfo.reloadPageToUpdate') }}</span>
              </div>
            }
          }
        </div>
        @if (status === 'downloading') {
          <ta-progress-bar barCssClass="h-1" mode="indeterminate" [showValue]="false" />
        }
        @if (status === 'ready') {
          <div class="flex justify-end">
            <button size="sm" taButton type="button" variant="info" (click)="reload()">
              {{ translate('versionInfo.updateNow') }}
            </button>
          </div>
        }
      </div>
    </ng-template>
  `,
  styleUrls: ['./app.css'],
  host: {
    '[style.--scrollbar-with]': 'this.scrollBarWith()',
    '[style.--scrollbar-color]': 'this.scrollBarColor()',
    '(window:beforeunload)': 'this.beforeUnload()',
    '(window:resize)': 'this.onResize($event)'
  },
  imports: [
    Navbar,
    AbsoluteLoader,
    RouterOutlet,
    TranslocoDirective,
    Splitter,
    SplitterPanel,
    ProgressBar,
    Button,
    Icon
  ]
})
export class App implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly currentUrl = injectCurrentUrl();
  private readonly activatedOutlets = injectActivatedOutlets();
  private readonly injector = inject(Injector);
  private readonly logger = inject(LoggerService);
  private readonly keycloak = inject(Keycloak);
  private readonly versionUpdateToast =
    viewChild.required<TemplateRef<VersionUpdateToastContext>>('versionUpdateToast');
  private readonly firebaseNotificationsService = inject(FirebasePushMgr);
  private readonly networkService = inject(NetworkListener);
  private readonly cachedLoggedOperator = injectEnsureCachedLoggedOperator();
  private readonly tokenBroadcastCh = inject(TOKEN_BROADCAST_DI_TOKEN);
  private readonly notificationsQueque = new QuequeMgr<{ notification: UserNotificationOmit; showPush: boolean }>(
    ({ notification, showPush }) => {
      this.notificationHandler(notification, showPush);
    },
    60000
  );
  private readonly routeEmbedded = toSignal(
    buildNavigationEndStream(this.router).pipe(
      switchMap(() => {
        return this.activatedRoute.queryParamMap.pipe(map((params) => params.get('embedded') === 'true'));
      })
    )
  );
  private readonly storageEmbedded = getFromStorage<boolean>('sessionStorage', StorageKeysEnum.embedded);
  private readonly tokenBroadcastChMessages = toSignal(
    this.tokenBroadcastCh.messagesOfType(BroadcastMessageType.UPDATE_TOKEN),
    { initialValue: null }
  );
  private readonly lsTokenUpdate = getFromStorage<Nullable<number>>('localStorage', StorageKeysEnum.updateToken);

  private readonly sidePanelActivated = computed(() => {
    if (this.loggedOperator() == null) {
      return false;
    }
    return this.activatedOutlets().includes(AvailableOutlets.RightPanel);
  });

  private notificationsService: Nullable<IndexedDbNotificationApi>;
  private latestPanelRoute: Nullable<'tag-ai' | 'notifications'>;

  /**
   * Determina se l'applicazione è eseguita in modalità embedded (nasconde navbar e footer).
   *
   * La modalità embedded viene attivata tramite il query param `?embedded=true` nella URL.
   * Il valore viene sincronizzato con sessionStorage per garantire persistenza durante la navigazione.
   *
   * Comportamento:
   * - Durante il primo caricamento, `routeEmbedded()` è `undefined` quindi la navbar viene nascosta preventivamente
   * - Una volta risolto il query param:
   *   - Se `embedded=true`: navbar e footer rimangono nascosti
   *   - Se `embedded=false` o assente: navbar e footer vengono mostrati
   * - Se sessionStorage contiene `embedded=true` ma il query param è diverso, prevale sessionStorage
   *
   * @returns `true` se in modalità embedded, `false` altrimenti, `undefined` durante la fase di inizializzazione
   */
  protected readonly embedded = computed(() => {
    if (this.routeEmbedded() !== this.storageEmbedded() && this.storageEmbedded()) {
      return true;
    }
    return this.routeEmbedded();
  });
  protected readonly showNavbar = computed(() => {
    return this.embedded() === false && !isFullscreenAuthRoute(this.currentUrl());
  });
  protected readonly isLoadingRoute = injectRouteLoading(PRIMARY_OUTLET, {
    requireShowAppLoader: true,
    minVisibleMs: ROUTE_LOADING_MIN_VISIBLE_MS
  });
  protected readonly scrollBarWith = signal('auto');
  protected readonly scrollBarColor = signal('unset');
  protected readonly splitPanelDisplay = computed(() => (this.sidePanelActivated() ? 'flex' : 'none'));
  protected readonly panelSizes = signal<number[]>([70, 30]);
  protected readonly minPanelSizes = signal<number[]>([20, 20]);
  protected readonly slideInTopAnimation = slideInTop;

  protected readonly systemOffline = computed(() => this.networkService.isOffline());
  protected readonly loggedOperator = signal<Nullable<QueryOperatorDTOExtended>>(null);

  constructor() {
    // Messaggio di avviso per chi apre la console
    const warningTitleCSS = 'color:red; font-size:60px; font-weight: bold; -webkit-text-stroke: 1px black;';
    const warningDescCSS = 'font-size: 18px;';

    if (inject(DeviceDetectorService).browser() === 'Firefox') {
      // this.scrollBarWith.set('thin');
      this.scrollBarColor.set('var(--tag-border-strong) var(--tag-surface)');
    }

    /* eslint-disable no-console */
    console.log('%cStop!', warningTitleCSS);
    console.log(
      '%cThis is a browser feature intended for developers. If someone told you to copy and paste something here to enable a feature or "hack" system, it is a scam. Interacting with the console violates our system policies.',
      warningDescCSS
    );
    console.log(
      '%cQuesta è una funzionalità per sviluppatori. Se qualcuno ti ha detto di copiare e incollare qualcosa qui per attivare una feature o per "hackerare" il sistema, è uno scam. Interagire con la console viola le nostre policy di sistema.',
      warningDescCSS
    );

    injectVersionUpdateToast(this.versionUpdateToast);

    keycloakAuthHandlerEffect();
    injectSessionKeepAlive();

    const recoverSession = injectSilentSsoRecovery();
    const invalidateFailedQueries = injectInvalidateFailedQueries();

    /*
      Una scheda che ha già perso il proprio token non ha nulla da rinnovare: l'annuncio di un'altra scheda è
      l'occasione per tentare il rientro.
    */
    const recoverAfterOtherTabRenewed = async (): Promise<void> => {
      const recovered = await recoverSession();
      if (recovered) {
        await invalidateFailedQueries();
      }
    };

    effect(() => {
      const payload = this.tokenBroadcastChMessages()?.payload ?? this.lsTokenUpdate();
      if (!payload) {
        return;
      }

      if (!this.keycloak.authenticated) {
        void recoverAfterOtherTabRenewed();
        return;
      }

      this.keycloak.updateToken(20).catch((error: unknown) => {
        this.logger.warn(`Rinnovo del token non riuscito dopo l'annuncio di un'altra scheda: ${String(error)}`);
      });
    });

    injectApplyBroadcastInvalidations();

    const initialDevToolsEnabled =
      getFromStorage<Nullable<boolean>>('sessionStorage', StorageKeysEnum.TanstackDev)() === true ||
      Config.branch === 'alpha';
    const enableTsDevTools = toSignal(
      fromEvent<KeyboardEvent>(document, 'keydown').pipe(
        map((event): boolean => event.ctrlKey && event.altKey && event.key === '.'),
        scan((acc, curr) => (curr ? !acc : acc), initialDevToolsEnabled)
      ),
      { initialValue: initialDevToolsEnabled }
    );
    effect(() => {
      toggleDevTools(enableTsDevTools());
    });

    effect(() => {
      const msg = this.firebaseNotificationsService.messageEvent();
      if (msg) {
        const { message, fromServiceWorker } = msg;
        const notification: UserNotificationOmit = {
          messageId: message.messageId ?? '',
          content: message,
          readed: 0,
          viewed: 0
        };
        this.notificationHandler(notification, !fromServiceWorker);
      }
    });

    this.logger.warn('Not possible to persist storage');
    tryPersistWithoutPromtingUser().then((persist) => {
      switch (persist) {
        case 'never':
          this.logger.warn('Not possible to persist storage');
          break;
        case 'persisted':
          this.logger.info('Successfully persisted storage silently');
          break;
        case 'prompt':
          this.logger.warn('Persistent storage disabled, prompt may be sent to user.');
          break;
      }
    });

    this.disableWindowDragEvents();
  }

  public ngOnInit(): void {
    this.cachedLoggedOperator().then((operator) => {
      if (!operator) {
        this.logger.error('Operatore non trovato', undefined, 'App');
        return;
      }
      this.loggedOperator.set(operator);
      if (!isDevMode()) {
        this.firebaseNotificationsService.requestPermission(operator.id);
      }
      runInInjectionContext(this.injector, async () => {
        this.notificationsService = inject(IndexedDbNotificationApi);
        await this.notificationsService.insertOrUpdateLastNotificationTime();
        await this.notificationsService.syncNotifications();
      });
    });

    this.updatePanelSizes(window.innerWidth);
  }

  public beforeUnload(): void {
    // svuoto i ticket data usati dal nuovo ticket
    removeFromStorage('sessionStorage', StorageKeysEnum.newTicketData);
  }

  public onResize(event: Event) {
    const width = (event.target as Window).innerWidth;
    this.updatePanelSizes(width);
  }

  protected async toggleNotificationsHandler(): Promise<void> {
    if (this.splitPanelDisplay() === 'none' || this.latestPanelRoute !== 'notifications') {
      this.latestPanelRoute = 'notifications';
      this.notificationsService?.markAllNotificationsAsViewed();
      const res = await this.router.navigate([{ outlets: { 'r-panel': ['notifications'] } }], {
        queryParamsHandling: 'merge'
      });
      if (!res) {
        // Significa che la rotta è già navigata quindi chiudo il pannello.
        // Questo caso può avvenire solo al primo caricamento con il pannello già visibile
        this.closeSplitPanel();
      }
    } else {
      this.closeSplitPanel();
    }
  }

  protected async toggleAiChatHandler(): Promise<void> {
    if (this.splitPanelDisplay() === 'none' || this.latestPanelRoute !== 'tag-ai') {
      this.latestPanelRoute = 'tag-ai';
      const res = await this.router.navigate([{ outlets: { 'r-panel': ['tag-ai'] } }], {
        queryParamsHandling: 'merge'
      });
      if (!res) {
        // Significa che la rotta è già navigata quindi chiudo il pannello.
        // Questo caso può avvenire solo al primo caricamento con il pannello già visibile
        this.closeSplitPanel();
      }
    } else {
      this.closeSplitPanel();
    }
  }

  private closeSplitPanel() {
    this.router.navigate([{ outlets: { 'r-panel': null } }], { queryParamsHandling: 'merge' }).then(() => {
      this.latestPanelRoute = null;
    });
  }

  private async notificationHandler(notification: UserNotificationOmit, showPush: boolean) {
    notification.content.data.timestamp = normalizeBackendTimestamp(notification.content.data.timestamp);
    // Allineamento guid con body di notifica derivante da backend.
    if (notification.content.data.messageID) {
      notification.messageId = notification.content.data.messageID;
    }
    if (this.notificationsService) {
      const viewed = this.latestPanelRoute === 'notifications' && this.splitPanelDisplay() === 'flex' ? 1 : 0;
      if ((await this.notificationsService.addNotification({ ...notification, viewed })) && showPush) {
        const { data } = notification.content;
        this.firebaseNotificationsService.serviceWorker?.showNotification(data.notificationSubject, {
          body: data.plainTextString,
          icon: `${self.location.origin}/tagappweb/assets/img/favicon.ico`,
          data
        });
      }
    } else {
      this.notificationsQueque.enqueue({ notification, showPush });
    }
  }

  private disableWindowDragEvents(): void {
    window.ondragover = (e: DragEvent) => {
      if (e.dataTransfer && !e.defaultPrevented) {
        e.preventDefault();
        e.dataTransfer.effectAllowed = 'none';
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    };

    window.ondragenter = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    window.ondrop = (e: DragEvent) => {
      e.preventDefault();
      return false;
    };

    window.ondragstart = (e: DragEvent) => {
      const target = e.target as Nullable<HTMLElement>;
      if (target && (!target.hasAttribute('draggable') || target.getAttribute('draggable') !== 'true')) {
        e.preventDefault();
        return false;
      }
      return true;
    };
  }

  private updatePanelSizes(width: number) {
    const notificationCount = this.notificationsService?.notifications().length ?? 0;
    const is1000 = notificationCount >= 1000;
    const is10000 = notificationCount >= 10000;
    const is100000 = notificationCount >= 100000;
    if (width <= 576) {
      this.panelSizes.set([1, 99]);
      this.minPanelSizes.set([0, 99]);
    } else if (width <= 768) {
      this.panelSizes.set(is10000 ? [30, 70] : [35, 65]);
      this.minPanelSizes.set(is10000 ? [0, 70] : [0, 65]);
    } else if (width <= 1024) {
      this.panelSizes.set(is100000 ? [45, 55] : [50, 50]);
      this.minPanelSizes.set(is100000 ? [0, 55] : [20, 50]);
    } else if (width <= 1535) {
      this.panelSizes.set(is1000 ? [60, 40] : [65, 35]);
      this.minPanelSizes.set(is1000 ? [20, 40] : [20, 35]);
    } else {
      this.panelSizes.set(is100000 ? [70, 30] : [75, 25]);
      this.minPanelSizes.set(is100000 ? [20, 30] : [20, 25]);
    }
  }
}
